// File: /src/components/ProjectsForm.js

import React, { useState } from 'react';
import '../styles/ProjectsForm.css';

const ProjectsForm = ({
	form,
	handleInputChange,
	addOrUpdateProject,
	clients,
	editingProject,
}) => {
	const [dateError, setDateError] = useState({ start: '', end: '' });
	const [dateInputValue, setDateInputValue] = useState({
		start: form.start_date
			? new Date(form.start_date).toLocaleDateString('pt-BR')
			: '',
		end: form.end_date
			? new Date(form.end_date).toLocaleDateString('pt-BR')
			: '',
	});
	const [timeInputValue, setTimeInputValue] = useState({
		start: form.start_date
			? new Date(form.start_date).toLocaleTimeString('pt-BR', {
					hour: '2-digit',
					minute: '2-digit',
			  })
			: '10:00',
		end: form.end_date
			? new Date(form.end_date).toLocaleTimeString('pt-BR', {
					hour: '2-digit',
					minute: '2-digit',
			  })
			: '12:00',
	});

	// Função que formata a data enquanto o usuário digita (inserindo "/" automaticamente)
	const formatDateInput = (value) => {
		const digits = value.replace(/\D/g, '');
		let formatted = digits;
		if (digits.length > 2) {
			formatted = digits.slice(0, 2) + '/' + digits.slice(2);
		}
		if (digits.length > 4) {
			formatted =
				digits.slice(0, 2) +
				'/' +
				digits.slice(2, 4) +
				'/' +
				digits.slice(4, 8);
		}
		return formatted;
	};

	// Valida se a data está no formato DD/MM/AAAA e se é uma data válida
	const validateDate = (dateStr) => {
		if (dateStr.length === 0) return true;
		const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
		if (!regex.test(dateStr)) return false;
		const [, day, month, year] = dateStr.match(regex);
		const date = new Date(year, month - 1, day);
		return (
			date &&
			date.getFullYear() === Number(year) &&
			date.getMonth() === Number(month) - 1 &&
			date.getDate() === Number(day)
		);
	};

	const handleDateTimeChange = (type, value) => {
		if (type === 'start' || type === 'end') {
			const formatted = formatDateInput(value);
			setDateInputValue((prev) => ({ ...prev, [type]: formatted }));

			if (!validateDate(formatted)) {
				setDateError((prev) => ({
					...prev,
					[type]: 'Data inválida',
				}));
				handleInputChange({
					target: {
						name: type === 'start' ? 'start_date' : 'end_date',
						value: null,
					},
				});
			} else {
				setDateError((prev) => ({ ...prev, [type]: '' }));
				updateDateTime(type, formatted, timeInputValue[type]);
			}
		}
	};

	const handleTimeChange = (type, value) => {
		setTimeInputValue((prev) => ({ ...prev, [type]: value }));
		if (dateInputValue[type]) {
			updateDateTime(type, dateInputValue[type], value);
		}
	};

	const updateDateTime = (type, dateStr, timeStr) => {
		if (dateStr.length === 10 && timeStr) {
			const [day, month, year] = dateStr.split('/');
			const [hours, minutes] = timeStr.split(':');

			const date = new Date(
				Number(year),
				Number(month) - 1,
				Number(day),
				Number(hours),
				Number(minutes)
			);

			handleInputChange({
				target: {
					name: type === 'start' ? 'start_date' : 'end_date',
					value: date.toISOString(),
				},
			});
		}
	};

	return (
		<form
			className="projects-form"
			onSubmit={addOrUpdateProject}
			autoComplete="off"
		>
			<h2 className="form-title">
				{editingProject ? 'EDITAR PROJETO' : 'ADICIONAR PROJETO'}
			</h2>

			<div className="form-field">
				<label>Cliente</label>
				<select
					name="clientId"
					value={form.clientId}
					onChange={(e) =>
						handleInputChange({
							target: {
								name: 'clientId',
								value: Number(e.target.value),
							},
						})
					}
					required
				>
					<option value="">Selecione um Cliente</option>
					{clients.map((client) => (
						<option key={client.id} value={String(client.id)}>
							{client.id} - {client.name}
						</option>
					))}
				</select>
			</div>

			<div className="form-field">
				<label>Nome do Projeto</label>
				<input
					name="name"
					placeholder="Digite o nome do projeto"
					value={form.name}
					onChange={handleInputChange}
					required
				/>
			</div>

			<div className="form-field">
				<label>Descrição</label>
				<textarea
					name="description"
					placeholder="Digite a descrição do projeto"
					value={form.description ?? ''}
					onChange={handleInputChange}
					rows={3}
				/>
			</div>

			<div className="form-field">
				<label>Valor</label>
				<input
					name="value"
					type="number"
					placeholder="Digite o valor"
					value={form.value}
					onChange={handleInputChange}
					required
				/>
			</div>

			<div className="form-field">
				<div className="datetime-input-group">
					<div className="datetime-field">
						<label>Data Inicial</label>
						<input
							type="text"
							placeholder="DD/MM/AAAA"
							value={dateInputValue.start}
							onChange={(e) =>
								handleDateTimeChange('start', e.target.value)
							}
							autoComplete="off"
							maxLength="10"
							required
						/>
						{dateError.start && (
							<span className="date-error">
								{dateError.start}
							</span>
						)}
					</div>
					<div className="datetime-field">
						<label>Hora</label>
						<input
							type="time"
							step="60"
							value={timeInputValue.start}
							onChange={(e) =>
								handleTimeChange('start', e.target.value)
							}
							required
							min="00:00"
							max="23:59"
						/>
					</div>
				</div>
			</div>

			<div className="form-field">
				<div className="datetime-input-group">
					<div className="datetime-field">
						<label>Data Final</label>
						<input
							type="text"
							placeholder="DD/MM/AAAA"
							value={dateInputValue.end}
							onChange={(e) =>
								handleDateTimeChange('end', e.target.value)
							}
							autoComplete="off"
							maxLength="10"
							required
						/>
						{dateError.end && (
							<span className="error">{dateError.end}</span>
						)}
					</div>
					<div className="datetime-field">
						<label>Hora</label>
						<input
							type="time"
							step="60"
							value={timeInputValue.end}
							onChange={(e) =>
								handleTimeChange('end', e.target.value)
							}
							required
							min="00:00"
							max="23:59"
						/>
					</div>
				</div>
			</div>

			<div className="form-field">
				<label>Status</label>
				<select
					name="status"
					value={form.status}
					onChange={handleInputChange}
					required
				>
					<option value="pending">Pendente</option>
					<option value="in_progress">Em Andamento</option>
					<option value="completed">Concluído</option>
				</select>
			</div>

			<button type="submit">
				{editingProject ? 'Salvar Alterações' : 'Adicionar Projeto'}
			</button>
		</form>
	);
};

export default ProjectsForm;
